import { cx } from '@emotion/css'
import {
    DataLayerEventName,
    Election,
    NavClickedEvent,
    useFeature,
} from '@news-mono/web-common'
import React, { Fragment, useCallback } from 'react'
import { breakpoint } from '../../__styling'
import {
    NavigationContainer,
    StyledPicture,
    StyledLink,
    LogoLink,
    Heading,
} from './TNElectionNavigation.styled'

export type TNElectionNavigationProps = {
    election: Election
    activePage?: 'updates' | 'electorate-tracker'
    headingLevel?: 'h1' | 'h2'
    onEvent?: (event: NavClickedEvent) => void
    className?: string
}

export const TNElectionNavigation = ({
    election,
    activePage,
    headingLevel,
    onEvent,
    className,
}: TNElectionNavigationProps) => {
    const { isEnabled, isLandingPageEnabled, isElectorateTrackerPageEnabled } =
        useElectionNavigationToggles(election)

    let updatesLink: string
    let ariaLabel: string
    let alt: string

    if (election.type === 'federal') {
        updatesLink = `/politics/federal-election-${election.year}`
        ariaLabel = `Go to the Federal Election ${election.year} Homepage`
        alt = `Federal Election ${election.year}`
    } else {
        updatesLink = `/politics/${election.state}-election-${election.year}`
        ariaLabel = `Go to the ${election.state.toUpperCase()} Election ${
            election.year
        } Homepage`
        alt = `${election.state.toUpperCase()} Election ${election.year}`
    }

    const electoratesLink = `${updatesLink}/electorates`

    const handleUpdatesNavigation = useCallback(
        () =>
            onEvent?.(
                getNavEvent({ navText: 'Updates', navLink: updatesLink }),
            ),
        [onEvent, updatesLink],
    )

    const handleElectorateTrackerNavigation = useCallback(
        () =>
            onEvent?.(
                getNavEvent({
                    navText: 'Electorate Tracker',
                    navLink: electoratesLink,
                }),
            ),
        [onEvent, electoratesLink],
    )

    if (!isEnabled) {
        return null
    }

    const picture = (
        <StyledPicture>
            <source
                media={breakpoint('sm').replace('@media ', '')}
                srcSet={election.logo.desktop}
            />
            <img src={election.logo.mobile} alt={alt} />
        </StyledPicture>
    )

    return (
        <NavigationContainer className={className}>
            <Heading as={headingLevel ?? Fragment}>
                {isLandingPageEnabled ? (
                    <LogoLink
                        to={updatesLink}
                        aria-label={ariaLabel}
                        onClick={handleUpdatesNavigation}
                    >
                        {picture}
                    </LogoLink>
                ) : (
                    picture
                )}
            </Heading>
            {isLandingPageEnabled && (
                <StyledLink
                    to={updatesLink}
                    className={cx([activePage === 'updates' && 'active'])}
                    onClick={handleUpdatesNavigation}
                >
                    Updates
                </StyledLink>
            )}
            {isElectorateTrackerPageEnabled && (
                <StyledLink
                    to={electoratesLink}
                    className={cx([
                        activePage === 'electorate-tracker' && 'active',
                    ])}
                    onClick={handleElectorateTrackerNavigation}
                >
                    Electorate Tracker
                </StyledLink>
            )}
        </NavigationContainer>
    )
}

type ElectionNavigationToggles = {
    isEnabled: boolean
    isLandingPageEnabled: boolean
    isElectorateTrackerPageEnabled: boolean
}

const useElectionNavigationToggles = (
    election: Election,
): ElectionNavigationToggles => {
    const federalIsEnabled = useFeature('federal-election-navigation')
    const federalLandingPageEnabled = useFeature(
        'federal-election-landing-page-route',
    )
    const federalElectorateTrackerPageEnabled = useFeature(
        'federal-election-electorate-tracker-route',
    )

    const waIsEnabled = useFeature('wa-election-navigation')
    const waLandingPageEnabled = useFeature('wa-election-landing-page-route')
    const waElectorateTrackerPageEnabled = useFeature(
        'wa-election-electorate-tracker-route',
    )

    if (election.type === 'federal') {
        return {
            isEnabled: federalIsEnabled,
            isLandingPageEnabled: federalLandingPageEnabled,
            isElectorateTrackerPageEnabled: federalElectorateTrackerPageEnabled,
        }
    } else {
        return {
            isEnabled: waIsEnabled,
            isLandingPageEnabled: waLandingPageEnabled,
            isElectorateTrackerPageEnabled: waElectorateTrackerPageEnabled,
        }
    }
}

type GetNavEventArgs = { navText: string; navLink: string }

/**
 * Creates a nav event for TNElectionNavigation.
 */
const getNavEvent = ({
    navText,
    navLink,
}: GetNavEventArgs): NavClickedEvent => ({
    type: DataLayerEventName.navClicked,
    originator: 'TNElectionNavigation',
    payload: {
        navName: 'ElectionNavigation',
        navLocation: 'OnPage',
        navText,
        navLink,
    },
})
